/**
 * InvoicesPage — the tenant's generated invoices (most-recent first).
 *
 * Phase 12 / Task 12.3. Reads GET /v1/billing/invoices. A row opens the detail
 * view at /invoices/:id. Money via formatUsdFromCents; dates via the local
 * timestamp formatter (DEC-004 — never bare UTC). VIEW_INVOICES is enforced by
 * the API (three-layer security); this list is read-only.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { StatusLabel } from 'src/components/ui/status-label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { SortableHead } from 'src/components/ui/sortable-head';

import { formatUsdFromCents } from '../lib/money';
import { sortRows, useUrlSort } from '../lib/url-state';
import { formatDate, formatCreated } from '../lib/timestamp';
import { useUserTimezone } from '../lib/use-user-timezone';
import { useInvoices, type InvoiceSummary } from '../features/billing/hooks';
import { AccountingExportDownload } from '../features/billing/AccountingExportDownload';
import { invoiceStatusTone } from '../features/billing/status-tone';

/** The columns the Invoices table sorts by (OPEN-204); anything else in ?sort= is ignored. */
const INVOICE_SORTS = ['number', 'operator', 'period', 'total', 'generated'] as const;

export function InvoicesPage(): React.JSX.Element {
  const navigate = useNavigate();
  const tz = useUserTimezone();
  const { data, isLoading, isError, refetch } = useInvoices();

  // OPEN-204 — click a header to sort. The default is the API's order, most recent first.
  const { sort, toggle } = useUrlSort(INVOICE_SORTS, { key: 'generated', dir: 'desc' }, []);
  const rows: readonly InvoiceSummary[] = sortRows(data?.invoices ?? [], sort, (inv, k) => {
    switch (k) {
      case 'number':
        return inv.invoiceNumber;
      case 'operator':
        return inv.operatorName;
      case 'period':
        return inv.periodStart;
      case 'total':
        return inv.totalCents;
      case 'generated':
        return inv.generatedAt;
    }
  });

  return (
    <div style={{ padding: '24px', maxWidth: '1080px' }}>
      {/* Rendered unconditionally, ABOVE the list: a failed or empty invoice
          list must not hide the export. The old Setup & Configuration placement
          was hidden by exactly that kind of early return. */}
      <AccountingExportDownload />

      {isLoading && (
        <p role="status" style={{ color: 'var(--color-text-secondary)' }}>
          Loading invoices…
        </p>
      )}

      {isError && (
        <div style={{ color: 'var(--color-danger)' }}>
          <p style={{ marginBottom: '8px' }}>Couldn&apos;t load invoices.</p>
          <Button
            variant="outline"
            onClick={(): void => {
              void refetch();
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <p style={{ color: 'var(--color-text-secondary)' }}>
          No invoices yet. Generate one from the Pipeline.
        </p>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <div
          style={{
            borderRadius: 'var(--radius-card)',
            backgroundColor: 'var(--color-bg-secondary)',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead column="number" sort={sort} onSort={toggle}>
                  Invoice #
                </SortableHead>
                <SortableHead column="operator" sort={sort} onSort={toggle}>
                  Operator
                </SortableHead>
                <SortableHead column="period" sort={sort} onSort={toggle}>
                  Period
                </SortableHead>
                <TableHead>Status</TableHead>
                <SortableHead className="text-right" column="total" sort={sort} onSort={toggle}>
                  Total
                </SortableHead>
                <SortableHead column="generated" sort={sort} onSort={toggle}>
                  Generated
                </SortableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((inv) => (
                <TableRow
                  key={inv.id}
                  onClick={(): void => {
                    void navigate(`/invoices/${inv.id}`);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell style={{ fontWeight: 600 }}>{inv.invoiceNumber}</TableCell>
                  <TableCell>
                    {inv.operatorName ?? (
                      <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDate(inv.periodStart, tz)} – {formatDate(inv.periodEnd, tz)}
                  </TableCell>
                  <TableCell>
                    <StatusLabel tone={invoiceStatusTone(inv.status)}>{inv.status}</StatusLabel>
                  </TableCell>
                  <TableCell className="text-right">{formatUsdFromCents(inv.totalCents)}</TableCell>
                  <TableCell>{formatCreated(inv.generatedAt, tz)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
